import React, { useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, BarChart3, Target, Shield, TrendingUp, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { isPersonalEmail, PERSONAL_EMAIL_MESSAGE } from '@/lib/email-validation';
import { identify, trackEvent } from '@/lib/visitor-tracking';

const PLAYBOOK_URL = '/resources/The_CRO_Pipeline_Playbook_2026.pdf';

const insightsPreview = [
  'Why 73% of CRM data is wrong and how AI fixes it',
  'The evidence-based forecasting framework used by top CROs',
  'How to cut 8+ hours of admin per rep per week',
  'MEDDIC scoring automation: from manual to real-time',
  'Building a pipeline review cadence that actually works',
  'Pipeline intelligence benchmarks: what evidence-based forecasting looks like',
];

const whoItsFor = [
  { icon: BarChart3, label: 'CROs', desc: 'defending their number with evidence, not opinions' },
  { icon: Target, label: 'VP Sales', desc: 'scaling their team without scaling admin' },
  { icon: TrendingUp, label: 'RevOps', desc: 'building a data-driven revenue machine' },
  { icon: Shield, label: 'Sales Ops', desc: 'eliminating CRM hygiene problems for good' },
];

export default function LeadMagnet() {
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [name, setName] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;

    if (isPersonalEmail(email)) {
      setEmailError(PERSONAL_EMAIL_MESSAGE);
      toast({ title: 'Work email required', description: PERSONAL_EMAIL_MESSAGE, variant: 'destructive' });
      return;
    }
    setEmailError(null);
    setIsSubmitting(true);

    try {
      // Log the lead
      await supabase.from('website_visits').insert({
        visitor_id: localStorage.getItem('noq5_vid') || 'unknown',
        session_id: sessionStorage.getItem('noq5_sid') || 'unknown',
        page_path: '/resources/cro-pipeline-playbook',
        page_title: 'CRO Pipeline Playbook Download',
        referrer: document.referrer || null,
        device_type: window.innerWidth < 768 ? 'mobile' : 'desktop',
        browser: navigator.userAgent.includes('Chrome') ? 'Chrome' : 'Other',
        os: navigator.userAgent.includes('Win') ? 'Windows' : 'Other',
        screen_resolution: `${screen.width}x${screen.height}`,
        language: navigator.language,
      });

      // Notify founder
      await supabase.functions.invoke('notify-founder', {
        body: {
          type: 'lead_magnet_download',
          name,
          email,
          company: company || 'Not provided',
          resource: 'CRO Pipeline Playbook 2026',
        },
      });

      setIsSubmitted(true);
      identify(email, { name, company: company || undefined, via: 'form' }).catch(() => {});
      trackEvent('playbook_download', { resource: 'CRO Pipeline Playbook 2026' }).catch(() => {});
      toast({ title: 'Your playbook is ready!', description: 'Download it below. We also sent a copy to your inbox.' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or contact us directly.', variant: 'destructive' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="The CRO Pipeline Playbook 2026 | Free Download | NoQ5.ai"
        description="How top CROs use AI to automate CRM updates, score pipelines with evidence, and run forecasts that boards trust. Free playbook for revenue leaders."
        canonicalUrl="https://noq5.ai/resources/cro-pipeline-playbook"
        keywords="CRO playbook, pipeline management guide, revenue forecasting, AI CRM guide, sales pipeline best practices, CRO dashboard, evidence-based forecasting"
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "The CRO Pipeline Playbook 2026",
          "description": "How top CROs use AI to automate CRM updates, score pipelines with evidence, and run forecasts that boards trust.",
          "publisher": { "@type": "Organization", "name": "NoQ5.ai" }
        }]}
      />
      <Breadcrumbs items={[{ label: "Resources", href: "/resources/cro-pipeline-playbook" }, { label: "CRO Pipeline Playbook", href: "/resources/cro-pipeline-playbook" }]} />

      <div className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left: Content */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Download className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">Free Resource</span>
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
                The CRO Pipeline Playbook <span className="text-primary">2026</span>
              </h1>

              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                The definitive guide for revenue leaders who want to stop chasing data and start making decisions. 
                Learn how the best CROs use AI to automate CRM hygiene, score deals on evidence, 
                and run forecast calls that boards actually trust.
              </p>

              <h2 className="text-xl font-bold mb-4">What you will learn</h2>
              <ul className="space-y-3 mb-10">
                {insightsPreview.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <h2 className="text-xl font-bold mb-4">Who this is for</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {whoItsFor.map((persona, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-card border border-border rounded-xl">
                    <persona.icon className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-foreground">{persona.label}</p>
                      <p className="text-sm text-muted-foreground">{persona.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:sticky lg:top-24"
            >
              <div className="bg-card border-2 border-primary/20 rounded-2xl p-8 shadow-lg">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Your playbook is ready!</h3>
                    <p className="text-muted-foreground mb-6">
                      Click below to download. We also sent a copy to your inbox.
                    </p>
                    <Button className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold mb-4" asChild>
                      <a href={PLAYBOOK_URL} download target="_blank" rel="noopener noreferrer">
                        <Download className="mr-2 w-4 h-4" /> Download the Playbook
                      </a>
                    </Button>
                    <br />
                    <Button variant="outline" className="mt-2" asChild>
                      <a href="/?scrollTo=demo">
                        See NoQ5 in Action <ArrowRight className="ml-2 w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold mb-2">Get the playbook</h3>
                    <p className="text-muted-foreground mb-6">Free. No credit card. Straight to your inbox.</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">Full name *</label>
                        <Input
                          id="name"
                          type="text"
                          required
                          placeholder="Jane Smith"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="h-12"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">Work email *</label>
                        <Input
                          id="email"
                          type="email"
                          required
                          placeholder="jane@company.com"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError(null);
                          }}
                          onBlur={() => {
                            if (email && isPersonalEmail(email)) {
                              setEmailError(PERSONAL_EMAIL_MESSAGE);
                            }
                          }}
                          aria-invalid={!!emailError}
                          aria-describedby={emailError ? 'email-error' : undefined}
                          className={`h-12 ${emailError ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                        />
                        {emailError && (
                          <p id="email-error" className="mt-1.5 text-sm text-destructive">{emailError}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-sm font-medium text-foreground mb-1.5">Company</label>
                        <Input
                          id="company"
                          type="text"
                          placeholder="Acme Corp"
                          value={company}
                          onChange={(e) => setCompany(e.target.value)}
                          className="h-12"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold h-12 text-base"
                      >
                        {isSubmitting ? 'Sending...' : 'Download the Playbook'}
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        We respect your privacy. No spam, ever.
                      </p>
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
